import { useMemo, useState, useCallback, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Provider as UrqlProvider, Client, cacheExchange, fetchExchange, subscriptionExchange, useQuery, useSubscription } from "urql"
import { createClient as createWSClient } from "graphql-ws"
import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "@/components/ui/sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { ProgressRing } from "@/components/shared/progress-ring"
import { EmptyState } from "@/components/shared/empty-state"
import { sectionTitleClass } from "@/components/shared/field-label"
import { Webhook, Mail, FileJson, Building2 } from "lucide-react"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import {
  EndpointsQuery,
  MessagesQuery,
  OutboundDeliveriesQuery,
  OutboundDeliveryUpdatedSubscription,
  MessageReceivedSubscription,
} from "@/lib/graphql/outbound"
import { EventTypeSchemasQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

function createPortalClient(token: string): { client: Client; dispose: () => void } {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:"
  const wsUrl = `${proto}//${window.location.host}/graphql`
  const wsClient = createWSClient({
    url: wsUrl,
    lazy: true,
    retryAttempts: 5,
    connectionParams: () => ({ authorization: `Bearer ${token}` }),
  })

  const client = new Client({
    url: "/graphql",
    requestPolicy: "cache-and-network",
    exchanges: [
      cacheExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query ?? "" }
          return {
            subscribe(sink) {
              const unsubscribe = wsClient.subscribe(input, sink as Parameters<typeof wsClient.subscribe>[1])
              return { unsubscribe }
            },
          }
        },
      }),
      fetchExchange,
    ],
    fetchOptions: () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
  })

  return { client, dispose: () => wsClient.dispose() }
}

export function PortalPage() {
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""

  const portal = useMemo(() => createPortalClient(token), [token])
  useEffect(() => {
    return () => portal.dispose()
  }, [portal])

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Invalid portal link. A token is required.</p>
      </div>
    )
  }

  return (
    <UrqlProvider value={portal.client}>
      <ThemeProvider>
        <div className="min-h-screen bg-background">
          <div className="mx-auto max-w-6xl px-6 py-8">
            <PortalContent />
          </div>
        </div>
        <Toaster />
      </ThemeProvider>
    </UrqlProvider>
  )
}

type EndpointItem = ResultOf<typeof EndpointsQuery>["endpoints"]["nodes"][number]
type MessageItem = ResultOf<typeof MessagesQuery>["messages"]["nodes"][number]
type DeliveryItem = ResultOf<typeof OutboundDeliveriesQuery>["outboundDeliveries"]["nodes"][number]
type SchemaItem = ResultOf<typeof EventTypeSchemasQuery>["eventTypeSchemas"]["nodes"][number]

const endpointColumns: Column<EndpointItem>[] = [
  {
    header: "URL",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-75 truncate block">
        {row.type === "HTTP" ? row.url : "\u2014"}
      </code>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Health",
    cell: (row) => {
      if (row.disabledReason) {
        return (
          <Badge variant="destructive" className="text-2xs">
            Disabled: {row.disabledReason}
          </Badge>
        )
      }
      const score = row.healthScore
      const color = score >= 0.8 ? "bg-emerald-500" : score >= 0.4 ? "bg-amber-500" : "bg-red-500"
      return (
        <div className="flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
          <span className="text-xs tabular-nums">{Math.round(score * 100)}%</span>
        </div>
      )
    },
  },
  {
    header: "Rate Limit",
    cell: (row) => (
      <span className="text-xs text-muted-foreground tabular-nums">
        {row.rateLimitRps > 0 ? `${row.rateLimitRps} rps` : "unlimited"}
      </span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

const messageColumns: Column<MessageItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Idempotency Key",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-40 truncate block">
        {row.idempotencyKey}
      </code>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

const deliveryColumns: Column<DeliveryItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.message.eventType}</code>,
  },
  {
    header: "Endpoint",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-50 truncate block">
        {row.endpoint.url}
      </code>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <ProgressRing value={row.attempts} max={row.maxAttempts} size={28} strokeWidth={2.5} />
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

const schemaColumns: Column<SchemaItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <span className="text-xs font-mono font-medium">{row.eventType}</span>,
  },
  {
    header: "Description",
    cell: (row) => (
      <span className="text-xs text-muted-foreground max-w-80 truncate block">
        {row.description || "\u2014"}
      </span>
    ),
  },
  {
    header: "Schema",
    cell: (row) => (
      <span className="text-xs text-muted-foreground">
        {row.schema ? "Defined" : "\u2014"}
      </span>
    ),
  },
  {
    header: "Updated",
    cell: (row) => <TimeAgo date={row.updatedAt} />,
  },
]

const LIMIT = 25

function PortalContent() {
  const [tab, setTab] = useState("endpoints")

  const endpointPagination = useCursorPagination(LIMIT, [])
  const messagePagination = useCursorPagination(LIMIT, [])
  const deliveryPagination = useCursorPagination(LIMIT, [])
  const schemaPagination = useCursorPagination(LIMIT, [])

  const [endpoints] = useQuery({
    query: EndpointsQuery,
    variables: { after: endpointPagination.after, first: endpointPagination.first },
  })

  const [messages, reexecuteMessages] = useQuery({
    query: MessagesQuery,
    variables: { first: messagePagination.first, after: messagePagination.after },
  })

  const [deliveries, reexecuteDeliveries] = useQuery({
    query: OutboundDeliveriesQuery,
    variables: { first: deliveryPagination.first, after: deliveryPagination.after },
  })

  const [schemas] = useQuery({
    query: EventTypeSchemasQuery,
    variables: { first: schemaPagination.first, after: schemaPagination.after },
  })

  const handleDeliveryUpdate = useCallback(
    (
      _prev: ResultOf<typeof OutboundDeliveryUpdatedSubscription> | undefined,
      response: ResultOf<typeof OutboundDeliveryUpdatedSubscription>,
    ) => {
      if (response?.outboundDeliveryUpdated) {
        reexecuteDeliveries({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecuteDeliveries],
  )

  const handleNewMessage = useCallback(
    (
      _prev: ResultOf<typeof MessageReceivedSubscription> | undefined,
      response: ResultOf<typeof MessageReceivedSubscription>,
    ) => {
      if (response?.messageReceived) {
        reexecuteMessages({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecuteMessages],
  )

  useSubscription({ query: OutboundDeliveryUpdatedSubscription }, handleDeliveryUpdate)
  useSubscription({ query: MessageReceivedSubscription }, handleNewMessage)

  const appName = endpoints.data?.endpoints.nodes[0]?.application?.name

  if (endpoints.error && messages.error) {
    return (
      <EmptyState
        title="Unable to load portal"
        description="The portal token may have expired or is invalid."
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary text-primary-foreground font-bold text-sm">
          H
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">
              {appName ?? "Customer Portal"}
            </h1>
            {appName && (
              <Badge variant="secondary" className="text-2xs">
                <Building2 className="h-3 w-3 mr-1" />
                Portal
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Manage your webhook endpoints, view messages, and monitor deliveries
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-border/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Webhook className="h-3.5 w-3.5" />
              Endpoints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {endpoints.data?.endpoints.pageInfo.total ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {messages.data?.messages.pageInfo.total ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <FileJson className="h-3.5 w-3.5" />
              Event Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold tabular-nums">
              {schemas.data?.eventTypeSchemas.pageInfo.total ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="catalog">Event Catalog</TabsTrigger>
          <TabsTrigger value="messages">Recent Messages</TabsTrigger>
          <TabsTrigger value="deliveries">Delivery Status</TabsTrigger>
        </TabsList>

        <TabsContent value="endpoints" className="mt-4">
          <Card className="border-border/30">
            <CardHeader>
              <CardTitle className={sectionTitleClass}>Endpoints</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={endpointColumns}
                data={endpoints.data?.endpoints.nodes ?? []}
                keyFn={(row) => row.id}
                cursorInfo={endpoints.data?.endpoints.pageInfo}
                cursorCallbacks={endpointPagination}
                pageSize={LIMIT}
                loading={endpoints.fetching}
                emptyTitle="No endpoints"
                emptyDescription="No webhook endpoints configured for this application"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="catalog" className="mt-4">
          <Card className="border-border/30">
            <CardHeader>
              <CardTitle className={sectionTitleClass}>Event Type Catalog</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={schemaColumns}
                data={schemas.data?.eventTypeSchemas.nodes ?? []}
                keyFn={(row) => row.id}
                cursorInfo={schemas.data?.eventTypeSchemas.pageInfo}
                cursorCallbacks={schemaPagination}
                pageSize={LIMIT}
                loading={schemas.fetching}
                emptyTitle="No event types"
                emptyDescription="No event type schemas have been defined yet"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="messages" className="mt-4">
          <Card className="border-border/30">
            <CardHeader>
              <CardTitle className={sectionTitleClass}>Recent Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={messageColumns}
                data={messages.data?.messages.nodes ?? []}
                keyFn={(row) => row.id}
                cursorInfo={messages.data?.messages.pageInfo}
                cursorCallbacks={messagePagination}
                pageSize={LIMIT}
                loading={messages.fetching}
                emptyTitle="No messages"
                emptyDescription="No messages have been sent to this application yet"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <Card className="border-border/30">
            <CardHeader>
              <CardTitle className={sectionTitleClass}>Delivery Status</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={deliveryColumns}
                data={deliveries.data?.outboundDeliveries.nodes ?? []}
                keyFn={(row) => row.id}
                cursorInfo={deliveries.data?.outboundDeliveries.pageInfo}
                cursorCallbacks={deliveryPagination}
                pageSize={LIMIT}
                loading={deliveries.fetching}
                emptyTitle="No deliveries"
                emptyDescription="No delivery attempts have been made yet"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
